import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Dialog } from "@rio-cloud/rio-uikit";
import { fetchBudgetHistory, fetchBudgetUserProfile, IBudgetVersion } from "../../api/fetchBudget";
import { getRouteByProfile, getKanbanRouteByProfile } from "./budgetCard/utils";
import { BudgetHistory } from "../budgetsForm/types";
interface BudgetHistoryModalProps {
  show: boolean;
  budgetId: string;
  onClose: () => void;
  consultant: string;
}

const BudgetHistoryModal = ({
  show,
  budgetId,
  consultant,
  onClose,
}: BudgetHistoryModalProps) => {
  const navigate = useNavigate();
  const [versions, setVersions] = useState<BudgetHistory[]>([]);
  const [currentVersion, setCurrentVersion] = useState<BudgetHistory | null>(
    null
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show && budgetId) {
      loadVersions();
    }
  }, [show, budgetId]);

  const loadVersions = async () => {
    setLoading(true);
    try {
      const response = await fetchBudgetHistory(
        budgetId,
        "versionNumber",
        "desc",
        1,
        50,
        true
      );
      setVersions(response.data);
      if (response.data.length > 0) {
        setCurrentVersion(response.data[0]);
      }
    } catch (error) {
      console.error("[BudgetHistoryModal] Erro ao carregar versões:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (
      statusLower.includes("enviado") ||
      statusLower.includes("sent") ||
      statusLower === "enviado"
    ) {
      return "bg-blue-100 text-blue-700";
    }
    if (
      statusLower.includes("aprovado") ||
      statusLower.includes("approved")
    ) {
      return "bg-green-100 text-green-700";
    }
    if (
      statusLower.includes("recusado") ||
      statusLower.includes("rejected") ||
      statusLower.includes("rejeitado")
    ) {
      return "bg-red-100 text-red-700";
    }
    if (statusLower.includes("criado") || statusLower.includes("created")) {
      return "bg-gray-100 text-gray-700";
    }
    return "bg-gray-100 text-gray-700";
  };

  const handleViewBudget = async (version: BudgetHistory) => {
    if (!budgetId) return;

    try {
      const userProfile = await fetchBudgetUserProfile();
      const baseRoute = getRouteByProfile(userProfile);
      const kanbanRoute = getKanbanRouteByProfile(userProfile);
      
      if (!baseRoute) {
        console.error("[BudgetHistoryModal] Perfil do usuário não identificado");
        return;
      }

      const route = `${baseRoute}/${budgetId}/versions/${version.version}${kanbanRoute ? `?redirectBack=${encodeURIComponent(kanbanRoute)}` : ''}`;
      navigate(route);
      onClose();
    } catch (error) {
      console.error("[BudgetHistoryModal] Erro ao redirecionar:", error);
    }
  };

  const getRecipientLabel = (recipient: string): string => {
    if (recipient === "CONSULTANT") {
      return "Consultor da concessionária";
    }
    if (recipient === "FACTORY") {
      return "Volks|total";
    }
    if (recipient === "MANAGER") {
      return "Gestor de frota";
    }
    return recipient;
  };

  return (
    <>
      <Dialog
        show={show}
        title="Histórico de atualizações"
        onClose={onClose}
        bsSize={Dialog.SIZE_LG}
        footer={
          <div className="display-flex justify-content-end">
            <Button bsStyle="default" onClick={onClose}>
              Fechar
            </Button>
          </div>
        }
        body={
          loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="spinnerInfoBox display-flex justify-content-center align-items-center">
                <div className="spinner"></div>
                <div className="spinnerText">Carregando histórico...</div>
              </div>
            </div>
          ) : (
            <div className="p-4">
              {currentVersion && (
                <div className="mb-6">
                  <h3 className="text-size-16 font-semibold mb-4">
                    Histórico de atualizações
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                            Consultor da concessionária
                          </th>
                          <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                            Destinatário
                          </th>
                          <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                            Orçamento
                          </th>
                          <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                            Versão
                          </th>
                          <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                            Data
                          </th>
                          <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-gray-200">
                          <td className="px-4 py-3 text-size-14 text-gray-800">
                            {consultant}
                          </td>
                          <td className="px-4 py-3 text-size-14 text-gray-800">
                            {getRecipientLabel(currentVersion.user)}
                          </td>
                          <td className="px-4 py-3 text-size-14">
                            <button
                              onClick={() => handleViewBudget(currentVersion)}
                              className="text-blue-600 hover:text-blue-800 underline flex items-center gap-2"
                            >
                              <span className="rioglyph rioglyph-file"></span>
                              <span>Visualizar orçamento</span>
                            </button>
                          </td>
                          <td className="px-4 py-3 text-size-14 text-gray-800">
                            v {currentVersion.version}.0
                          </td>
                          <td className="px-4 py-3 text-size-14 text-gray-800">
                            {currentVersion.versionDate}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`px-3 py-1 rounded-full text-size-12 font-medium ${getStatusBadgeClass(currentVersion.status)}`}
                            >
                              {currentVersion.status}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="mt-6">
                <h3 className="text-size-16 font-semibold mb-4">
                  Histórico de alterações:
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                          Perfil
                        </th>
                        <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                          Ver orçamento
                        </th>
                        <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                          Versão
                        </th>
                        <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                          Data da versão
                        </th>
                        <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {versions.map((version, index) => (
                        <tr
                          key={`${version.id}-${version.version}-${index}`}
                          className="border-b border-gray-200"
                        >
                          <td className="px-4 py-3 text-size-14 text-gray-800">
                            {version.profile}
                          </td>
                          <td className="px-4 py-3 text-size-14">
                            <button
                              onClick={() => handleViewBudget(version)}
                              className="text-blue-600 hover:text-blue-800 underline flex items-center gap-2"
                            >
                              <span className="rioglyph rioglyph-file"></span>
                              <span>Visualizar orçamento</span>
                            </button>
                          </td>
                          <td className="px-4 py-3 text-size-14 text-gray-800">
                            v {version.version}.0
                          </td>
                          <td className="px-4 py-3 text-size-14 text-gray-800">
                            {version.versionDate}
                          </td>
                          <td className="px-4 py-3 text-size-14 text-gray-800">
                            {version.status}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )
        }
      />
    </>
  );
};

export default BudgetHistoryModal;

